import { Injectable } from '@angular/core';
import { NativeDateAdapter } from '@angular/material/core';

/**
 * Angular Material's propio `NativeDateAdapter.parse()` delega en
 * `new Date(Date.parse(value))`. Cuando `value` es un string ISO de solo
 * fecha ("2026-01-01", sin componente de hora), el spec de ECMAScript lo
 * interpreta como medianoche UTC — no local. En cualquier zona horaria con
 * offset negativo (América, incluido Ecuador) eso corre el resultado un día
 * hacia atrás, y en enero además retrocede el año (31/12 del año anterior).
 *
 * `SolDatepickerComponent` (sol-datepicker.component.ts) ya parsea bien las
 * fechas en sus propios `writeValue()`/`_toIso()`/`_toDisplay()` — pero
 * expone el `<input [matDatepicker]>` real de Material bajo un input
 * oculto, y cualquier cosa que escriba texto ahí directamente (Cypress con
 * `.type()`, o un usuario tipeando en vez de usar el calendario) dispara
 * el parseo de Material, no el de este componente. Bug real, reproducido en
 * `cypress/e2e/periodos-contables.cy.ts` con fechas de enero.
 *
 * Fix: interceptar solo el caso "string ISO de solo fecha" y construirlo
 * con el constructor `new Date(year, monthIndex, day)`, que SIEMPRE es
 * local — igual que el resto de este componente. Cualquier otro valor
 * (objeto Date, string con hora, etc.) se delega sin tocar al parser
 * nativo de Material.
 */
@Injectable()
export class LocalNativeDateAdapter extends NativeDateAdapter {
  private static readonly ISO_DATE_ONLY = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;

  override parse(value: unknown): Date | null {
    if (typeof value === 'string') {
      const match = LocalNativeDateAdapter.ISO_DATE_ONLY.exec(value.trim());
      if (match) {
        const [, y, m, d] = match;
        const date = new Date(Number(y), Number(m) - 1, Number(d));
        return isNaN(date.getTime()) ? null : date;
      }
    }
    return super.parse(value);
  }
}
